<template>
  <!-- Closed: non-interactive "Closed" placeholders in place of Yes/No prices -->
  <div
    v-if="closed && useMiniCard"
    :class="[miniCardHorizontal ? 'flex flex-row gap-[8px] w-full' : 'flex flex-col gap-[2px] w-[104px]']"
    aria-label="Closed for trading"
  >
    <div
      :class="[
        'flex items-center justify-center px-[8px] h-[24px] bg-surface-low text-on-surface-tertiary text-[14px] leading-[18px] font-semibold select-none',
        miniCardHorizontal ? 'rounded-[4px] flex-1' : 'rounded-t-[4px]',
      ]"
    >
      {{ t('closed') }}
    </div>
    <div
      :class="[
        'flex items-center justify-center px-[8px] h-[24px] bg-surface-low text-on-surface-tertiary text-[14px] leading-[18px] font-semibold select-none',
        miniCardHorizontal ? 'rounded-[4px] flex-1' : 'rounded-b-[4px]',
      ]"
    >
      {{ t('closed') }}
    </div>
  </div>
  <div v-else-if="closed" class="font-medium flex gap-[6px] tabular-nums text-on-surface-tertiary">
    {{ t('closed') }}
  </div>
  <div
    v-else-if="useMiniCard"
    :class="[
      '[@media(hover:none)]:pointer-events-none',
      miniCardHorizontal ? 'flex flex-row gap-[8px] w-full' : 'flex flex-col gap-[2px] w-[104px]',
    ]"
  >
    <Button
      severity="secondary"
      ib-variant="yes"
      size="small"
      :class="[
        '!px-[8px] !h-[24px] !min-w-0 !justify-between !font-semibold [&_span]:!text-inherit',
        miniCardHorizontal ? '!rounded-[4px] flex-1' : '!rounded-t-[4px] !rounded-b-none',
        noHover &&
          'hover:!bg-(--p-button-yes-secondary-default-background) hover:!border-(--p-button-yes-secondary-default-background) hover:!text-(--p-yes)',
      ]"
      @click.stop="handleBidClick(yesConid)"
    >
      <span class="text-[14px] leading-[18px]">{{ $t('yes') }}</span>
      <span
        class="font-semibold text-[16px] leading-[20px] tabular-nums"
        aria-live="polite"
        :aria-label="t('yesPrice', { value: yesBid || t('noData') })"
      >
        {{ yesBid || '-' }}
      </span>
    </Button>
    <Button
      severity="secondary"
      ib-variant="no"
      size="small"
      :class="[
        '!px-[8px] !h-[24px] !min-w-0 !justify-between !font-semibold [&_span]:!text-inherit',
        miniCardHorizontal ? '!rounded-[4px] flex-1' : '!rounded-b-[4px] !rounded-t-none',
        noHover &&
          'hover:!bg-(--p-button-no-secondary-default-background) hover:!border-(--p-button-no-secondary-default-background) hover:!text-(--p-no)',
      ]"
      @click.stop="handleBidClick(noConid)"
    >
      <span class="text-[14px] leading-[18px]">{{ $t('no') }}</span>
      <span
        class="font-semibold text-[16px] leading-[20px] tabular-nums"
        aria-live="polite"
        :aria-label="t('noPrice', { value: noBid || t('noData') })"
      >
        {{ noBid || '-' }}
      </span>
    </Button>
  </div>
  <div class="font-medium flex gap-[6px] tabular-nums" v-else>
    <span
      class="dark:text-(--p-green100) text-(--p-green200)"
      aria-live="polite"
      :aria-label="t('yesPrice', { value: yesBid || t('noData') })"
      >{{ yesBid || '-' }}</span
    >
    <span class="text-muted-color" aria-hidden="true">/</span>
    <span
      class="dark:text-(--p-red100) text-(--p-red200)"
      aria-live="polite"
      :aria-label="t('noPrice', { value: noBid || t('noData') })"
      >{{ noBid || '-' }}</span
    >
  </div>
</template>

<script setup lang="ts">
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDollarPrice } from '@/utils/formatPrice';
import { useFlexbestBidAskStream } from '@/composables/useFlexbestBidAskStream';
import { useGetEventContractBidAsk, useGetFlexbestBidAsk } from '@/api/mdfarm.queries';
import { useOrderTicket } from '@/electionHub/composables/useOrderTicket';
import { useAppState } from '@/stores/useAppState';
import { usePriceSideSetting } from '@/composables/usePriceSideSetting';
import { useUserState } from '@/stores/useUserState';
import useContractActions from '@/composables/useContractActions';

export interface YesNoBidProps {
  yesConid: number;
  noConid: number;
  yesConidExPairs?: string[];
  noConidExPairs?: string[];
  exchange: string;
  multiExchange?: boolean;
  useMiniCard: boolean;
  /** When useMiniCard is true, lay Yes/No buttons horizontally instead of stacked */
  miniCardHorizontal?: boolean;
  priceSide?: 'bid' | 'ask';
  productConid?: number;
  /** Exchange paired with productConid for routing. Defaults to exchange. */
  routingExchange?: string;
  noHover?: boolean;
  /**
   * When false, live bid-ask streaming and REST polling are paused.
   * Defaults to true — callers that don't pass it keep streaming unconditionally.
   */
  isLive?: boolean;
  /** Seed ask price for YES side from product response (0-1) */
  seedAskYes?: number;
  /** Seed ask price for NO side from product response (0-1) */
  seedAskNo?: number;
  /** Seed bid price for YES side from product response (0-1) */
  seedBidYes?: number;
  /** Seed bid price for NO side from product response (0-1) */
  seedBidNo?: number;
  /** Allow REST price snapshots while authenticated, for Closing Soon. */
  useRest?: boolean;
  /** When true, render a non-interactive "Closed" state instead of Yes/No prices. */
  closed?: boolean;
}

const { t } = useI18n();
const props = withDefaults(defineProps<YesNoBidProps>(), { isLive: true });

const appState = useAppState();
const userState = useUserState();
const isPublicApi = computed(() => toValue(userState.usePublicApi));

const yesConidExPairs = computed(() => props.yesConidExPairs ?? [`${props.yesConid}@${props.exchange}`]);
const noConidExPairs = computed(() => props.noConidExPairs ?? [`${props.noConid}@${props.exchange}`]);

// ── Auth: flexbest WS stream (works for single or multi-exchange) ──
const yesStream = useFlexbestBidAskStream(yesConidExPairs, {
  seedBid: () => props.seedBidYes,
  seedAsk: () => props.seedAskYes,
  isLive: () => props.isLive,
});
const noStream = useFlexbestBidAskStream(noConidExPairs, {
  seedBid: () => props.seedBidNo,
  seedAsk: () => props.seedAskNo,
  isLive: () => props.isLive,
});

const yesHasSeed = computed(() => props.seedBidYes !== undefined || props.seedAskYes !== undefined);
const noHasSeed = computed(() => props.seedBidNo !== undefined || props.seedAskNo !== undefined);
const needsRest = computed(() => props.isLive && (isPublicApi.value || props.useRest));

const useYesFlexbestRest = computed(
  () => needsRest.value && !yesHasSeed.value && (props.yesConidExPairs?.length ?? 0) > 1,
);
const useNoFlexbestRest = computed(
  () => needsRest.value && !noHasSeed.value && (props.noConidExPairs?.length ?? 0) > 1,
);

const yesSingleQuery = useGetEventContractBidAsk(
  computed(() => (needsRest.value && !yesHasSeed.value && !useYesFlexbestRest.value ? props.yesConid : undefined)),
  computed(() => props.exchange),
);
const yesFlexbestQuery = useGetFlexbestBidAsk(
  computed(() => (useYesFlexbestRest.value ? props.yesConidExPairs : undefined)),
);
const noSingleQuery = useGetEventContractBidAsk(
  computed(() => (needsRest.value && !noHasSeed.value && !useNoFlexbestRest.value ? props.noConid : undefined)),
  computed(() => props.exchange),
);
const noFlexbestQuery = useGetFlexbestBidAsk(
  computed(() => (useNoFlexbestRest.value ? props.noConidExPairs : undefined)),
);

const yesRestData = computed(() =>
  useYesFlexbestRest.value ? yesFlexbestQuery.data.value : yesSingleQuery.data.value,
);
const noRestData = computed(() => (useNoFlexbestRest.value ? noFlexbestQuery.data.value : noSingleQuery.data.value));

// ── Display value resolution ──
const { priceSide: defaultPriceSide } = usePriceSideSetting();
const side = computed(() => props.priceSide ?? defaultPriceSide.value);

const yesDisplayValue = computed(() => {
  if (!isPublicApi.value || yesHasSeed.value) {
    const streamValue = side.value === 'bid' ? yesStream.bid.value : yesStream.ask.value;
    return streamValue ?? (props.useRest ? yesRestData.value?.[side.value] : null) ?? null;
  }
  return yesRestData.value?.[side.value] ?? null;
});

const noDisplayValue = computed(() => {
  if (!isPublicApi.value || noHasSeed.value) {
    const streamValue = side.value === 'bid' ? noStream.bid.value : noStream.ask.value;
    return streamValue ?? (props.useRest ? noRestData.value?.[side.value] : null) ?? null;
  }
  return noRestData.value?.[side.value] ?? null;
});

const yesBid = computed(() => formatDollarPrice(yesDisplayValue.value, true));
const noBid = computed(() => formatDollarPrice(noDisplayValue.value, true));
const { openOrderTicket } = useOrderTicket();
const actions = useContractActions();

const handleBidClick = (conid: number) => {
  if (appState.isScrolling) return;
  window.$apm?.logUserInteractionEvent('Card Click', 'contract-click', { origin: 'YesNoBidMiniCard' }, false);
  if (props.productConid) {
    // Open the product details page at the market level only. We intentionally
    // do NOT pass the clicked contract's conid — the page should land on its
    // default contract rather than deep-linking/pre-selecting the one clicked.
    let exchange = props.routingExchange ?? props.exchange;
    if (props.yesConidExPairs && props.yesConidExPairs.length > 0) {
      const [, firstExchange] = props.yesConidExPairs[0].split('@');
      exchange = firstExchange;
    }
    actions.openQuoteDetails(props.productConid, exchange);
    return;
  }
  const eventConidExs = props.multiExchange
    ? conid === props.yesConid
      ? props.yesConidExPairs
      : props.noConidExPairs
    : undefined;
  openOrderTicket(conid, undefined, props.multiExchange ? 'BEST' : undefined, eventConidExs);
};
</script>
<!-- last modified by AI tools on 2026-10-12 -->
